import { Component , Vue , Prop, Mixins } from 'vue-property-decorator';
import { HOMEWORKS_EVENTS, HOMEWORK_STATUS, SHOW_RESPONSE_TABS, _HOMEWORKS_STORE } from '@/homework/values/homeworks.values';
import { SECTION_TYPES } from '@/content/values/sections';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import KEYS from '@/values/keys';
import { Section } from '@/courses/models/section.model';
import { IEdTabsMenu } from '@/content/interfaces/ed-tabs-menu.interface';
import { ANNOTATION_TYPES } from '../values/courses.values';
import { HomeworkMixin } from '@/homework/mixins/homework.mixin';
import ToolService from '@/providers/tools';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { CourseToolsMixin } from './course-tools.mixin';

@Component({})
export class CourseViewerResponseSplitObjectMixin extends Mixins(HomeworkMixin, CourseToolsMixin) {

  /* Props */
  @Prop() readonly section: any;
  @Prop() readonly student: any;

  /* Data */
  public problem: Section = null;
  public solution: Section = null;
  public response: Section = null;
  public answer: Section = null;
  public menu: IEdTabsMenu[];
  public menuSplitFirst: IEdTabsMenu[];
  public menuSplitSecond: IEdTabsMenu[];
  public isSaving: boolean = false;
  public pdfLoaded: boolean = false;
  public pdfChanged: boolean = false;
  public pagesDrawn: number[] = [];
  public isSavedDraft = {
    pagesDrawn: [],
    status: false
  };
  public grades: any = null;
  public tabSelected: string = 'ProblemTab';
  public contentsId = {
    problem: 'ProblemTab',
    answer: 'AnswerTab',
    solution: 'SolutionTab',
    response: 'ResponseTab',
    splitVertical: 'SplitVertical',
    splitHorizontal: 'SplitHorizontal',
    problemTab: 'ProblemTab',
    answerTab: 'AnswerTab',
    solutionTab: 'SolutionTab',
    responseTab: 'ResponseTab',
  };

  /* Computed */
  get isOneViewSelected(): boolean {
    return (this.tabSelected != this.contentsId.splitVertical && this.tabSelected != this.contentsId.splitHorizontal);
  }

  get canSendFeedback(): boolean {
    return this.section.status && this.section.status != HOMEWORK_STATUS.GRADED && this.section.status != HOMEWORK_STATUS.FINISHED
        && this.section.status != HOMEWORK_STATUS.DRAFT;
  }

  get canSubmit() {
    if(this.pdfLoaded && !this.isSaving) {
      if(this.feedbackDraft) {
        return true;
      } else if (this.hasPagesDrawn) {
        return true;
      }
    }
    return false;
  }

  get canSendFeedbackDraft() {
    if(this.pdfLoaded && !this.isSaving) {
      if(this.feedbackDraft) {
        if(this.isSavedDraft.status && this.pdfChanged) { 
          return true;
        } else if (this.hasPagesDrawn) {
          return true;
        }
      } else if(this.hasPagesDrawn) {
        return true;
      } else if(this.isSavedDraft.status && this.pdfChanged) {
        return true;
      }
    }
    return false;
  }

  get homeworkId(): number {
    return this.section.section.homeworkId;
  }

  get hasPagesDrawn(): boolean {
    return this.pagesDrawn.length > 0;
  }

  get feedbackDraft() {
    return this.section.homeworkDraftAssetInfo ? this.section.homeworkDraftAssetInfo : null;
  }

  get url(): string {
    if(this.canSendFeedback) {
      if(this.feedbackDraft) {
        return this.feedbackDraft.url;
      } else if(this.response) {
        return this.response.assetsInfo[0].url;
      }
    } else {
      if(this.response) {
        return this.response.assetsInfo[0].url;
      }
    }
    return null;
  }

  get courseId(): number {
    return this.section.courseId;
  }

  get isShowResponseBoard() : boolean {
    return SHOW_RESPONSE_TABS.includes(this.tabSelected);
  }

  get pdfResponseUrl(): string {
    if(this.response && this.response.assetsInfo){
      return this.response.assetsInfo[0].pdfUrl ? this.response.assetsInfo[0].pdfUrl : this.response.assetsInfo[0].url;
    } else {
      return null;
    }
  }

  get viewerProblem() {
    let viewer:any = SECTION_TYPES.LESSON_PDF.viewer;
    if( this.problemMaterial ) {
      viewer = this.setCourseMaterialViewer(this.problemMaterial);
    }
    return viewer;
  }

  get problemMaterial(): any {
    return this.section.section.sections[0].sectionMaterial;
  }

  get viewerSolution() {
    let viewer:any = SECTION_TYPES.LESSON_PDF.viewer;
    if( this.solutionMaterial ) {
      viewer = this.setCourseMaterialViewer(this.solutionMaterial);
    }
    return viewer;
  }

  get solutionMaterial(): any {
    return this.section.section.sections[1].sectionMaterial;
  }

  /* Methods */
  onChangePdf({drawn, pagesDrawn}) {
    this.pdfChanged = true;
    this.pagesDrawn = pagesDrawn;
    this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.CHANGE_CONFIRM_CLOSE_HOMEWORK_VIEWER, true);
  }

  onLoadPdf() {
    this.pdfLoaded = true;
  }

  setMenu() {
    this.menu = [{
      name: String(this.$i18n.t('courses.writeObject.problemTab')),
      contentId: this.contentsId.problem,
      active: false,
      visible: false
    },{
      name: String(this.$i18n.t('courses.writeObject.responseTab')),
      contentId: this.contentsId.response,
      active: false,
      visible: false
    },{
      name: 'SplitHorizontal',
      contentId: this.contentsId.splitHorizontal,
      active: true,
      visible: false
    },{
      name: 'SplitVertical',
      contentId: this.contentsId.splitVertical,
      active: false,
      visible: false
    }];
    if(this.solution) {
      this.menu.splice(1, 0, {
        name: String(this.$i18n.t('courses.writeObject.solutionTab')),
        contentId: this.contentsId.solution,
        active: false,
        visible: false
      });
    }
    this.tabSelected = this.contentsId.splitHorizontal;
  }

  setMenuSplit() {
    this.menuSplitFirst = [{
      name: String(this.$i18n.t('courses.writeObject.problemTab')),
      contentId: this.contentsId.problemTab,
      active: true,
      visible: true
    }];
    if(this.solution) {
      this.menuSplitFirst.push({
        name: String(this.$i18n.t('courses.writeObject.solutionTab')),
        contentId: this.contentsId.solutionTab,
        active: false,
        visible: true
      });
    }
    this.menuSplitSecond = [{
      name: String(this.$i18n.t('courses.writeObject.responseTab')),
      contentId: this.contentsId.responseTab,
      active: true,
      visible: true
    }];
  }

  setFiles() {
    let sections = this.section.section.sections;
    this.response = this.section;
    this.setAnswer();
    if(sections) {
      sections.forEach((s) => {
        switch(s.type) {
          case SECTION_TYPES.SPLIT_QUESTION_PDF.code:
            this.problem = s;
          break;
          case SECTION_TYPES.SPLIT_SOLUTION_PDF.code:
            this.solution = s;
          break;
          case SECTION_TYPES.SPLIT_QUESTION_MATERIAL.code:
            this.problem = this.getSectionFromMaterial(s);
          break;
          case SECTION_TYPES.SPLIT_SOLUTION_MATERIAL.code:
            this.solution = this.getSectionFromMaterial(s);
          break;
        }
      });
    }
  }

  getSectionFromMaterial(doc: any) {
    return new Section({
      sectionId: doc.id,
      parentId: doc.parentId,
      courseId: this.section.courseId,
      classId: this.section.classId,
      name: doc.name,
      text: doc.sectionMaterial.materialAssetInfo.hash,
      type: SECTION_TYPES.LESSON_PDF.code,
      assetsInfo: [{
        url: doc.sectionMaterial.splicedUrl
      }]
    });
  }

  setAnswer() {
    if(this.section.section.assetsInfo) {
      this.answer = new Section({
        type: SECTION_TYPES.LESSON_PDF.code,
        assetsInfo: this.section.section.assetsInfo,
        text: this.section.section.text
      });
    }
  }

  setTab(contentId: string) {
    if (this.offlineMode) {
      return;
    }
    if(this.tabSelected !== contentId) {
      let el: any = this.$refs.EdViewerResponseWriteObjectTabs;
      switch(contentId) {
        case this.contentsId.splitVertical:
          this.setPositionTabs('Vertical');
          this.menu.forEach(m => m.visible = false);
        break;
        case this.contentsId.splitHorizontal:
          this.setPositionTabs('Horizontal');
          this.menu.forEach(m => m.visible = false);
        break;
        default:
          this.menu.forEach(m => {
            m.visible = (m.contentId !== this.contentsId.splitVertical && m.contentId !== this.contentsId.splitHorizontal);
            m.active = m.contentId == this.contentsId.problem;
          });
          this.setFirstTabItem('Problem');
        break;
      }
      el.setTab(contentId);
      this.tabSelected = contentId;
      this.$edEventBus.$emit(HOMEWORKS_EVENTS.CHANGE_SPLIT_TAB);
    }
  }

  setPositionTabs(position: string) {
    let problem: any = document.getElementById('Problem');
    let solution: any = document.getElementById('Solution');
    let response: any = document.getElementById('Response');
    let problemPosition = document.getElementById(`Problem${position}`);
    let solutionPosition = document.getElementById(`Solution${position}`);
    let responsePosition = document.getElementById(`Response${position}`);
    problemPosition?.appendChild(problem);
    solutionPosition?.appendChild(solution);
    responsePosition?.appendChild(response);
  }

  changeTab(tab: IEdTabsMenu) {
    switch(tab.contentId) {
      case this.contentsId.answer:
        this.setFirstTabItem('Answer');
      break;
      case this.contentsId.problem:
        this.setFirstTabItem('Problem');
      break;
      case this.contentsId.solution:
        this.setFirstTabItem('Solution');
      break;
      case this.contentsId.response:
        this.setFirstTabItem('Response');
      break;
    }
    this.tabSelected = tab.contentId;
  }

  setFirstTabItem(itemName: string) {
    let item: any = document.getElementById(itemName);
    let itemContainer = document.getElementById(`${itemName}Container`);
    itemContainer?.appendChild(item);
  }

  async confirmSendFeedback() {
    this.$EdModal.createGradeModal({ 
      action: (res: any) => {
        this.$EdToast.create({ msg: `${this.$i18n.t('common.sendingFeedback')}...` });
        setTimeout(() => {
          this.grades = res.grades;
          this.sendFeedback();
        }, 400);
      }
    });
  }

  public async confirmSendWhiteboardFeedback(boardGroupHash : string) {    
    let usersMadeAnnotations = await this.obtainUsersMadeAnnotations(boardGroupHash);
    if(!usersMadeAnnotations || usersMadeAnnotations.length === 0){
      this.openModalWithoutAnnotations();
      return;
    }

    this.$EdModal.createGradeModal({ 
      homeworkId: this.homeworkId,
      courseId: this.courseId,
      section: this.section,
      hasGrading: this.course.hasGrading,
      action: (res: any) => {
        this.$EdToast.create({ msg: `${this.$i18n.t('common.sendingFeedback')}...` });
        setTimeout(() => {
          this.grades = res.grades;
          this.sendWhiteboardHomeworkFeedback();
        }, 400);
      }
    });
  }

  async getFeedbackRequest() {
    let pdfDraw: any = this.$refs.EdPdfDraw;
    let file = await pdfDraw.getCurrentPdf();
    return this.buildRequest(file, SECTION_TYPES.LESSON_PDF.code);
  }

  private getWhiteBoardFeedbackRequest() {
    return this.buildRequest(null, SECTION_TYPES.LESSON_BOARD.code);
  }

  private buildRequest(file: any, type: string): any{
    let section: any = this.section;
    let data: any = {
      req: {
        name: section.name,
        id: section.id,
        type: type,
        classId: this.student.classId,
        file
      }, 
      student: this.student
    };
    if(this.grades) {
      if(this.course.hasGrading) {
        data.req.grades = this.grades;
      } else {
        data.req.grade = this.grades;
      }
    }
    return data;
  }

  async sendFeedback() {
    try {
      this.isSaving = true;
      let request = await this.getFeedbackRequest();
      await this.$store.dispatch(_HOMEWORKS_STORE.ACTIONS.SEND_FEEDBACK_TO_STUDENT, request);
      this.section.status = HOMEWORK_STATUS.GRADED;
      this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_WIDGET_TEACHER_PERSON, { 
        student: this.student, 
        homework: {
          ...this.section,
          homeworkAssetInfo: this.section.assetsInfo[0],
          section: {
            ...this.section.section,
            homeworkStatus: HOMEWORK_STATUS.GRADED
          }
        }
      });
      this.$edEventBus.$emit(HOMEWORKS_EVENTS.UPDATE_HOMEWORK_STATUS, this.section);
      this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.REFRESH_PERSON_STATUS, {student: this.student, isReload: false});
      this.$EdToast.update({ 
        msg: `${this.$i18n.t('common.sent')}`,
        type: TOAST_TYPES.SUCCESS,
        timeout: 1000
      });
      setTimeout(() => {
        this.isSaving = false;
      }, 1300);
      this.$EdModal.closeModal();
    } catch(err) {
      this.isSaving = false;
      this.$EdToast.hide();
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('courses.errors.errorTitle'),
        body: this.$i18n.t('courses.errors.errorResponseWriteObject'),
        type: KEYS.EVENT_TYPES.DANGER
      });
    }
  }

  public sendWhiteboardHomeworkFeedback() {
    this.isSaving = true;
    let request = this.getWhiteBoardFeedbackRequest();
    this.$store.dispatch(_HOMEWORKS_STORE.ACTIONS.SEND_FEEDBACK_TO_STUDENT, request)
      .then(response => {        
        this.section.status = HOMEWORK_STATUS.GRADED;        
        this.$edEventBus.$emit(HOMEWORKS_EVENTS.REALOAD_TEACHER_HOMEWORKS_FEEDBACK, {studentId: this.student.id, courseId: this.courseId});
        this.$EdToast.update({ 
          msg: `${this.$i18n.t('common.sent')}`,
          type: TOAST_TYPES.SUCCESS,
          timeout: 1000
        });
        this.$EdModal.closeModal();
      }).catch(error => {
        this.$EdToast.hide();
        this.$EdModal.createMessageModal({
          title: this.$i18n.t('courses.errors.errorTitle'),
          body: this.$i18n.t('courses.errors.errorResponseWriteObject'),
          type: KEYS.EVENT_TYPES.DANGER
        });
      }).finally(() => this.isSaving = false);    
  }

  async setFeedbackDraftAndExit() {
    await this.sendFeedbackDraft();
    this.$EdModal.closeModal();
  }

  async sendFeedbackDraft() {
    if(this.canSendFeedbackDraft) {
      try {
        this.isSaving = true;
        let req = await this.getFeedbackDraftRequest();
        this.$EdToast.create({ msg: `${this.$i18n.t('common.saving')}...` });
        await this.setFeedbackDraft(
          this.student,
          this.courseId,
          req.homework,
          req.file,
        );
        this.pdfChanged = false;
        if(!this.isSavedDraft.status) {
          this.isSavedDraft.status = true;
          this.isSavedDraft.pagesDrawn = this.pagesDrawn;
        }
        this.isSaving = false;
      } catch(err) {
        this.isSaving = false;
        this.$EdToast.hide();
        this.$EdModal.createMessageModal({
          title: this.$i18n.t('courses.errors.errorTitle'),
          body: this.$i18n.t('courses.errors.errorResponseWriteObject'),
          type: KEYS.EVENT_TYPES.DANGER
        });
      }
    }
  }

  async getFeedbackDraftRequest() {
    let pdfDraw: any = this.$refs.EdPdfDraw;
    let original = this.feedbackDraft ? this.feedbackDraft.hash : null;
    let currentPagesDrawn = pdfDraw.getDrawnPages();
    let pages = [];

    /* We check if the previous draft was changed */
    if(this.isSavedDraft.status) {
      currentPagesDrawn.forEach(p => {
        let found = this.isSavedDraft.pagesDrawn.findIndex(pd => pd == p) !== -1;
        if(!found) this.isSavedDraft.pagesDrawn.push(p);
      });
      pages = ToolService.orderByNumber(this.isSavedDraft.pagesDrawn);
    } else {
      pages = currentPagesDrawn;
    }

    let request: any = {
      homework: {
        name: this.section.name,
        classId: this.student.classId,
        sectionId: this.section.section.id,
        type: SECTION_TYPES.LESSON_PDF.code,
        responseOwnerId: this.student.id,
        metadata: {
          type: ANNOTATION_TYPES.PDF_ANNOTATION,
          pages
        }
      }, 
      file: pdfDraw.getPDFPages(pages)
    }
    if(original) request.homework.metadata.original = original;
    return request;
  }

  /* Hooks */ 
  beforeDestroy() {
    this.$edEventBus.$off(COURSES_EVENTS.SAVE_AND_CLOSE_HOMEWORK, this.setFeedbackDraftAndExit);
  }

  created() {
    this.$edEventBus.$on(COURSES_EVENTS.SAVE_AND_CLOSE_HOMEWORK, this.setFeedbackDraftAndExit);
    this.setFiles();
    this.setMenu();
    this.setMenuSplit();
  }

}